import * as Immutable from 'immutable';
import { Check } from '../../components/AlphaTestingStatus/TestingStatus/types';

export const biggerThan100Characters = (value: string | undefined) => {
  if (value && value.trim().length >= 100) {
    return Immutable.List();
  } else {
    return Immutable.fromJS(['field.required.100.characters']);
  }
};

export const POWER_POOL_DESCRIPTION_TEMPLATE = `Idea: 
Rationale for data used: 
Rationale for operators used: `;

// Should find all template lines in the same order.
// Requirements here: https://jira.worldquant.com/browse/BRAINTECH-4001
export const matchPowerPoolTemplate = (value: string | undefined) => {
  //two regex here, because test and exec influence each other
  const regex = /^\s*idea:\s+(.+?)\n\s*rationale for data used:\s+(.+?)\n\s*rationale for operators used:\s+(.+?)/gis;
  const groupRegex =
    /^\s*idea:(?<idea>\s+(.+?)\n)\s*rationale for data used:\s+(?<data>(.+?)\n)\s*rationale for operators used:(?<operators>\s+(.+?))/gis;
  const errorResult = Immutable.fromJS(['field.required.powerPool']);

  if (value) {
    const isMatched = regex.test(value);
    const exec = groupRegex.exec(value);
    const groups = [exec?.groups?.idea, exec?.groups?.data, exec?.groups?.operators];
    if (isMatched && groups.every(group => group?.trim() !== '')) {
      return Immutable.List();
    }
  }
  return errorResult;
};

export const getIsPowerPoolAlphaStatus = (alphaDetail: any) => {
  // Check if alpha has the power pool correlation check (Unsubmitted) or is classified as power pool (Submitted)
  return getIsPowerPoolAlpha(alphaDetail) || getIsClassifiedPowerPoolAlpha(alphaDetail);
};

export const getIsClassifiedPowerPoolAlpha = (alphaDetail: any) => {
  if (!alphaDetail) return false;

  const classifications = alphaDetail.get('classifications');
  return (
    classifications &&
    (classifications.toJS() as Array<{ id: string; name: string }>).some(
      classification => classification.id === 'POWER_POOL:POWER_POOL_ELIGIBLE'
    )
  );
};

export const getIsPowerPoolAlpha = (alphaDetail: any) => {
  if (!alphaDetail) return false;

  const checks = alphaDetail.getIn(['is', 'checks']).toJS() as Check[];
  const matchedCompetitionsCheck = checks.find(
    check => check.name === 'POWER_POOL_CORRELATION' && (check.result === 'PASS' || check.result === 'PENDING')
  );
  return Boolean(matchedCompetitionsCheck);
};

export const getAlphasRequiredProperties = (alphaDetail: any): any => {
  return Immutable.fromJS({
    regular: getIsPowerPoolAlpha(alphaDetail) ? [biggerThan100Characters, matchPowerPoolTemplate] : [],
    combo: [biggerThan100Characters],
    selection: [biggerThan100Characters],
  });
};

const invalidFieldsSuper = ['regular'];
const invalidFieldsRegular = ['combo', 'selection'];

export const validateFields = (alphaDetail: any, setAlphaProperties: (data: any) => any, setShowErrors?: (value: boolean) => any) => {
  let fields = ['color', 'name', 'tags', 'category', 'regular', 'combo', 'selection'];
  let data = {};
  let hasErrors = false;
  if (alphaDetail && alphaDetail.get('type') === 'SUPER') {
    fields = fields.filter(field => !invalidFieldsSuper.includes(field));
  } else {
    fields = fields.filter(field => !invalidFieldsRegular.includes(field));
  }
  fields.forEach((field: any) => {
    const arrayOfValidations = getAlphasRequiredProperties(alphaDetail).get(field);
    let errors = Immutable.List();
    if (arrayOfValidations && arrayOfValidations.size > 0) {
      errors = arrayOfValidations.reduce((totalErrors: any, validation: any) => {
        let validationErrors = Immutable.List();
        if (['regular', 'combo', 'selection'].includes(field)) {
          validationErrors = validation(alphaDetail.getIn([field, 'description']));
        } else {
          validationErrors = validation(alphaDetail.get(field));
        }

        return totalErrors.concat(validationErrors);
      }, Immutable.List());
    }
    if (errors.size > 0) {
      hasErrors = true;
    }
    if (field === 'tags') {
      let tags = alphaDetail && alphaDetail.get('tags');
      tags = tags && tags.size !== 0 ? tags : Immutable.List();
      data = { ...data, ...{ tags, errortags: errors } };
    } else if (field === 'combo' || field === 'selection' || field === 'regular') {
      const value = alphaDetail && alphaDetail.getIn([field, 'description']) ? alphaDetail.getIn([field, 'description']) : '';
      data = { ...data, ...{ [field]: { description: value, error: errors } } };
    } else {
      const value = alphaDetail && alphaDetail.get(field) !== null ? alphaDetail.get(field) : '';
      data = { ...data, ...{ [field]: value, [`error${field}`]: errors } };
    }
  });
  setAlphaProperties(data);
  if (setShowErrors) {
    setShowErrors(hasErrors);
  }

  return hasErrors;
};
